import { Button, IconButton } from '@mui/material';
import { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { WebsocketTypes } from '../../api/enumerations';
import { GetWorkOrdersParams } from '../../api/workOrders/types';
import Snackbar from '../../components/Snackbar';
import { PageHeader } from '../../components/UI/PageHeader';
import { Constants } from '../../constants/home';
import {
  IconAddMS,
  IconCheckCircleMS,
  IconCloseMS,
  IconEngineeringMS,
} from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { useWebsocketEvent } from '../../context/websocketMessage';
import { lazyNamed } from '../../helpers/lazyWithRetry';
import { useDebounce } from '../../hooks/useDebounce';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { BankSearch } from './BankSearch';
import { ViewModeToggle } from './components/ViewModeToggle';
import {
  buildParams,
  invalidateWorkOrders,
  usePipelineWorkOrdersQuery,
  useTopWorkOrdersQuery,
} from './hooks/queries';
import { useViewMode, ViewMode } from './hooks/useViewMode';
import { useWorkOrdersFlat } from './hooks/useWorkOrdersFlat';
import {
  HeaderTools,
  PageRoot,
  PlaceholderBox,
  StyledToast,
  ToastTypography,
} from './styles';
import { PipelineView } from './views/PipelineView';
import { TableView } from './views/TableView';

// Map and gallery views are not in the enabled modes yet; keep google maps
// and the gallery out of the initial chunk until a user switches to them.
const MapView = lazyNamed(
  () => import(/* webpackChunkName: "home-views" */ './views/MapView'),
  'MapView'
);
const GalleryView = lazyNamed(
  () => import(/* webpackChunkName: "home-views" */ './views/GalleryView'),
  'GalleryView'
);

export function Home(): JSX.Element {
  const [searchField, setSearchField] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [filters, setFilters] = useState<GetWorkOrdersParams>();

  const { openSnackbar } = useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(searchField);

  const enabledViewModes: ViewMode[] = ['pipeline', 'table'];

  const { viewMode, setViewMode } = useViewMode({
    enabledModes: enabledViewModes,
  });

  const flatViewActive =
    viewMode === 'table' || viewMode === 'map' || viewMode === 'gallery';

  const flatQuery = useWorkOrdersFlat({
    enabled: flatViewActive,
    searchField: debouncedSearch,
    filters,
  });

  // Pipeline: the "top" endpoint when nothing is being searched, the filtered
  // endpoint otherwise. Both cached by react-query, so coming back from an OS
  // shows the board instantly and the websocket invalidates on changes.
  const hasQuery = debouncedSearch.length > 0 || Boolean(filters);
  const params = useMemo(
    () => buildParams(debouncedSearch, filters),
    [debouncedSearch, filters]
  );
  const topQuery = useTopWorkOrdersQuery(!flatViewActive && !hasQuery);
  const pipelineQuery = usePipelineWorkOrdersQuery(
    params,
    !flatViewActive && hasQuery
  );
  const activeQuery = hasQuery ? pipelineQuery : topQuery;
  const pipelineData = activeQuery.data?.data;
  const loading = activeQuery.isLoading;

  const pipelineError = topQuery.error ?? pipelineQuery.error;
  useEffect(() => {
    if (pipelineError) handleSnackbar(getErrorMessage(pipelineError), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineError]);

  useWebsocketEvent<{ reference_number?: number }>(
    WebsocketTypes.AVM_PROCESSED,
    ({ payload }) => {
      const refNumber = payload?.reference_number;
      if (typeof refNumber === 'number') {
        setToastMessage(`OS ${refNumber} finalizou o cálculo.`);
        setOpenToast(true);
      }
      invalidateWorkOrders();
    }
  );

  const handleNewOs = (): void => {
    navigate('/home/property/new');
  };

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      {IconCloseMS}
    </IconButton>
  );

  const renderActiveView = (): JSX.Element => {
    if (viewMode === 'pipeline') {
      return (
        <PipelineView
          pipelineData={pipelineData}
          pipelineLoading={loading}
          isSearching={searchField.length > 0 || Boolean(filters)}
          filters={filters}
        />
      );
    }
    if (viewMode === 'table') {
      return (
        <TableView
          data={flatQuery.data}
          loading={flatQuery.loading}
          hasMore={flatQuery.hasMore}
          loadingMore={flatQuery.loadingMore}
          onLoadMore={flatQuery.loadMore}
        />
      );
    }
    if (viewMode === 'map') {
      return (
        <Suspense fallback={<PlaceholderBox />}>
          <MapView
            data={flatQuery.data}
            loading={flatQuery.loading}
            onSwitchToTable={() => setViewMode('table')}
          />
        </Suspense>
      );
    }
    if (viewMode === 'gallery') {
      return (
        <Suspense fallback={<PlaceholderBox />}>
          <GalleryView data={flatQuery.data} loading={flatQuery.loading} />
        </Suspense>
      );
    }
    return (
      <PlaceholderBox>
        {IconEngineeringMS}
        <p>essa visualização está em construção</p>
      </PlaceholderBox>
    );
  };

  return (
    <PageRoot fluid={viewMode === 'pipeline'}>
      <PageHeader
        title={Constants.bankTitle}
        subtitle={Constants.bankSubtitle}
        aside={
          <HeaderTools>
            <ViewModeToggle
              viewMode={viewMode}
              onChange={setViewMode}
              enabledModes={enabledViewModes}
            />
            <BankSearch
              searchField={searchField}
              setSearchField={setSearchField}
              setFilters={setFilters}
              searchLoading={loading || flatQuery.loading}
              isFiltersActive={!!filters}
            />
          </HeaderTools>
        }
        actions={
          <Button onClick={handleNewOs}>
            {IconAddMS}
            {Constants.buttonAdd}
          </Button>
        }
      />
      {renderActiveView()}
      {openSnackbar && <Snackbar />}
      <StyledToast
        open={openToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={handleClose}
        action={action}
        message={
          <ToastTypography>
            {IconCheckCircleMS}
            {toastMessage}
          </ToastTypography>
        }
      />
    </PageRoot>
  );
}
